import React, { useState, useEffect, useRef, useMemo } from 'react';
import DialogBox from '../common/DialogBox';
import apiService from '../../services/ApiService';
import { apiRoutes } from '../../shared/apiRoutes';
import { EyeIcon } from '@heroicons/react/24/outline';
import { formatDateOnly, pickGrandTotalProfitOrLoss, toISTDateString } from '../../utils/util';
import PartySodaTransactionsDialog from '../dialogs/PartySodaTransactionsDialog';
import { GameType } from '../../shared/enume';

// Simple Card wrapper component (matching Trial Balance)
const Card: React.FC<{ children: React.ReactNode; className?: string; style?: React.CSSProperties }> = ({ 
  children, 
  className = '', 
  style 
}) => (
  <div 
    className={`rounded-lg bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 border-none shadow-[0_8px_30px_rgb(0,0,0,0.12)] ${className}`}
    style={{ ...style, height: style?.height || '100%', maxHeight: style?.maxHeight || '100%' }}
  >
    {children}
  </div>
);

interface PartyAccountTransaction {
  id?: number;
  transactionId?: number;
  date: string;
  credit: number;
  debit: number;
  balance: number;
  remark?: string;
  transactionType?: string;
  matchId?: number;
  matchTypeId?: number;
  partyId?: number;
  havalaId?: number;
  otherPartyCode?: string;
  winOrLoss?: number | null; // Win/Loss value from API (will be negated for display)
  isTally?: boolean;
  isAllTally?: boolean;
  isConfirmed?: boolean;
  /** Match metadata when API returns `matchInfo` (same shape as Ledger) */
  matchInfo?: {
    gameId?: number;
    teamA?: string;
    teamB?: string;
    teamC?: string;
    matchType?: string;
  };
  teamA?: string;
  teamB?: string;
  teamC?: string;
}

interface PartyAccountData {
  partyCode: string;
  partyName: string;
  startingDate: string;
  endingDate: string;
  openingBalance: number;
  closingBalance: number;
  totalAmount: number;
  transactions: PartyAccountTransaction[];
  loseAndWin?: number;
}

interface PLMatch {
  gameId: number;
  matchLabel: string;
  gameType?: string;
  matchDate: string;
  totalCredit: number;
  totalDebit: number;
  profitOrLoss: number;
  result?: string;
}

interface PLReportData {
  reportType?: 'PL';
  partyCode?: string;
  partyName?: string;
  startingDate?: string;
  endingDate?: string;
  /** Authoritative total from API when present (`grandTotalProfitOrLoss`). */
  totalProfitOrLoss?: number;
  grandTotalProfitOrLoss?: number;
  matches?: PLMatch[];
}

interface PartyAccountDetailProps {
  isOpen: boolean;
  onClose: () => void;
  partyId: number;
  partyCode: string;
  partyName: string;
  amount: number;
  /** Trial balance party checkbox follows whether every included transaction is confirmed. */
  onTrialBalanceConfirmationChange?: (isConfirmed: boolean) => void;
}

const dateStringToTime = (d: string | undefined): number => {
  if (!d) return 0;
  const t = new Date(d).getTime();
  return Number.isFinite(t) ? t : 0;
};

/** Sort P&L matches ascending by date (then gameId) so running totals are chronological. */
const comparePlMatchDateAsc = (a: PLMatch, b: PLMatch): number => {
  const ta = dateStringToTime(a.matchDate);
  const tb = dateStringToTime(b.matchDate);
  if (ta !== tb) return ta - tb;
  return (a.gameId ?? 0) - (b.gameId ?? 0);
};

/** P&L API may include a synthetic match row for opening balance — used for ordering and display. */
const isPlOpeningBalanceMatch = (m: Record<string, unknown>): boolean => {
  const parts = [m.matchLabel, m.result, m.remark, m.gameType]
    .filter((x) => x != null && x !== '')
    .map((x) => String(x).toLowerCase());
  const blob = parts.join(' ');
  return blob.includes('opening balance') || /\bopening\s*bal\b/.test(blob);
};

const formatAmount = (value: number): string => {
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(value / 100));
};

/** Signed display for Dr−Cr difference (same scale as formatAmount: API minor units). */
const formatSignedProfitLoss = (drMinusCr: number): string => {
  const scaled = drMinusCr / 100;
  const sign = scaled < 0 ? '−' : scaled > 0 ? '' : '';
  return (
    sign +
    new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Math.abs(scaled))
  );
};

const PartyAccountDetail: React.FC<PartyAccountDetailProps> = ({
  isOpen,
  onClose,
  partyId,
  partyCode,
  partyName,
  amount,
  onTrialBalanceConfirmationChange,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reportData, setReportData] = useState<PartyAccountData | null>(null);
  const [loadedReportToken, setLoadedReportToken] = useState(0);
  const [plReportData, setPlReportData] = useState<PLReportData | null>(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
  const [sodaDialogOpen, setSodaDialogOpen] = useState(false);
  const [sodaDialogGameId, setSodaDialogGameId] = useState<number | string>(0);
  const [sodaDialogPartyId, setSodaDialogPartyId] = useState<number | string>(0);
  const [sodaDialogGameDetails, setSodaDialogGameDetails] = useState<{
    gameType?: string;
    teamA?: string;
    teamB?: string;
    teamC?: string;
  } | null>(null);
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const trialBalanceConfirmedRef = useRef<boolean | undefined>(undefined);

  const isPLReport = partyId === 0;

  /**
   * Opening-balance row(s) first, then other matches oldest-to-newest by matchDate.
   * Running balance follows that same ascending order.
   */
  const plRowsForDisplay = useMemo(() => {
    const matches = plReportData?.matches;
    if (!matches?.length) return [];

    const compareChronWithOpeningFirst = (a: PLMatch, b: PLMatch): number => {
      const aOpen = isPlOpeningBalanceMatch(a as unknown as Record<string, unknown>);
      const bOpen = isPlOpeningBalanceMatch(b as unknown as Record<string, unknown>);
      if (aOpen !== bOpen) return aOpen ? -1 : 1;
      return comparePlMatchDateAsc(a, b);
    };

    const chron = [...matches].sort(compareChronWithOpeningFirst);
    let acc = 0;
    return chron.map((m) => {
      acc += m.profitOrLoss || 0;
      return { match: m, runningThrough: acc };
    });
  }, [plReportData?.matches]);

  useEffect(() => {
    if (isOpen && (partyId === 0 || partyId)) {
      trialBalanceConfirmedRef.current = undefined;
      fetchPartyAccount();
    } else {
      setReportData(null);
      setPlReportData(null);
      setError(null);
    }
  }, [isOpen, partyId]);

  const fetchPartyAccount = async () => {
    setLoading(true);
    setError(null);
    try {
      // Starting date static as 1st June 2025 for Trial Balance party account report
      const startingDate = '2025-06-01';
      const today = new Date();
      const endingDate = toISTDateString(today);

      const startDateTime = `${startingDate}T00:00:00`;
      const endDateTime = `${endingDate}T23:59:59`;

      let response: any;
      if (isPLReport) {
        response = await apiService.post<PartyAccountData>(apiRoutes.reports.profitLossReport, {
          startingDate: startDateTime,
          endingDate: endDateTime,
        });
      } else {
        response = await apiService.post<PartyAccountData>(apiRoutes.reports.partyAccount, {
          partyId: partyId,
          startingDate: startDateTime,
          endingDate: endDateTime,
        });
      }

      const data = (response as any).data || response;

      if (data.reportType === 'PL' || isPLReport) {
        const matchesRaw = Array.isArray(data) ? data : (data.matches || []);
        const matches = matchesRaw as PLMatch[];
        const sumMatches = matches.reduce((sum: number, m: any) => sum + (m.profitOrLoss || 0), 0);

        const apiObj = Array.isArray(data) ? null : (data as Record<string, unknown>);
        const totalProfitOrLoss = pickGrandTotalProfitOrLoss(apiObj ?? data, sumMatches);

        const plBase: Record<string, unknown> = apiObj ? { ...apiObj } : { reportType: 'PL' };
        delete plBase.netOpeningBalance;
        delete plBase.NetOpeningBalance;

        setPlReportData({
          ...plBase,
          matches,
          totalProfitOrLoss,
          startingDate: (apiObj?.startingDate as string | undefined) ?? startDateTime,
          endingDate: (apiObj?.endingDate as string | undefined) ?? endDateTime,
        } as PLReportData);
        setReportData(null);
      } else {
        // Map transactions to include display fields
        const mappedTransactions: PartyAccountTransaction[] = (data.transactions || []).map((tx: any) => {
          const mapped: PartyAccountTransaction = {
            ...tx,
            transactionId: tx.transactionId ?? tx.id,
            date: tx.date || tx.transactionDate || '',
            credit: tx.credit || tx.totalCredits || 0,
            debit: tx.debit || tx.totalDebits || 0,
            balance: tx.balance || tx.finalAmount || 0,
            remark: tx.remark || tx.remarks || '',
            winOrLoss: tx.winOrLoss !== null && tx.winOrLoss !== undefined ? tx.winOrLoss : null,
            isConfirmed: tx.isConfirmed === true,
            partyId: tx.partyId ?? partyId,
          };
          if (tx.matchInfo) {
            mapped.matchInfo = tx.matchInfo;
            mapped.matchId = tx.matchInfo.gameId ?? tx.matchId;
            mapped.teamA = tx.matchInfo.teamA;
            mapped.teamB = tx.matchInfo.teamB;
            mapped.teamC = tx.matchInfo.teamC;
          } else if (tx.matchId != null) {
            mapped.matchId = tx.matchId;
          }
          return mapped;
        });
        const openingRaw = data.openingBalance ?? data.OpeningBalance;
        const openingBalance =
          openingRaw !== undefined && openingRaw !== null && openingRaw !== ''
            ? Number(openingRaw)
            : undefined;

        setReportData({
          ...data,
          openingBalance: Number.isFinite(openingBalance as number) ? (openingBalance as number) : 0,
          transactions: mappedTransactions,
        });
        setPlReportData(null);
      }
      setLoadedReportToken((token) => token + 1);
      setLoading(false);
    } catch (err: any) {
      console.error('Error fetching party account:', err);
      setError(err?.message || 'Failed to load party account details');
      setLoading(false);
    }
  };

  /** Win/Loss label from row Cr vs Dr: credit &gt; debit → Win, debit &gt; credit → Loss. */
  const getLossWinFromCrDr = (
    credit: number,
    debit: number,
  ): { label: string; tone: 'win' | 'loss' | 'neutral' } => {
    const cr = credit ?? 0;
    const dr = debit ?? 0;
    if (cr > dr) return { label: 'Win', tone: 'win' };
    if (dr > cr) return { label: 'Loss', tone: 'loss' };
    return { label: '-', tone: 'neutral' };
  };

  const getBalanceColor = (balance: number) => {
    if (balance < 0) return 'text-red-600 dark:text-red-400';
    if (balance > 0) return 'text-emerald-600 dark:text-emerald-400';
    return 'text-slate-900 dark:text-slate-100';
  };

  const rememberTrialBalanceConfirmation = (isConfirmed: boolean | undefined) => {
    if (typeof isConfirmed === 'boolean') {
      trialBalanceConfirmedRef.current = isConfirmed;
    }
  };

  const handleDialogClose = () => {
    if (typeof trialBalanceConfirmedRef.current === 'boolean') {
      onTrialBalanceConfirmationChange?.(trialBalanceConfirmedRef.current);
    }
    onClose();
  };

  const handleTransactionConfirmChange = async (transaction: PartyAccountTransaction, isConfirmed: boolean) => {
    if (!reportData) return;
    const gameId = transaction.matchId || transaction.matchInfo?.gameId;
    const rowPartyId = transaction.partyId ?? partyId;
    try {
      if (gameId && rowPartyId) {
        const result = await apiService.post<{ trialBalanceConfirmed?: boolean }>(
          apiRoutes.transactions.confirmGame,
          { partyId: rowPartyId, gameId, isConfirmed },
        );
        setReportData((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            transactions: prev.transactions.map((t) => {
              const rowGameId = t.matchId || t.matchInfo?.gameId;
              return rowGameId === gameId ? { ...t, isConfirmed } : t;
            }),
          };
        });
        rememberTrialBalanceConfirmation(result?.trialBalanceConfirmed);
        return;
      }

      const transactionId = transaction.transactionId ?? transaction.id;
      if (transactionId == null) return;
      const result = await apiService.post<{ trialBalanceConfirmed?: boolean }>(
        apiRoutes.transactions.confirm,
        { transactionId, isConfirmed },
      );
      setReportData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          transactions: prev.transactions.map((t) =>
            (t.transactionId ?? t.id) === transactionId ? { ...t, isConfirmed } : t
          ),
        };
      });
      rememberTrialBalanceConfirmation(result?.trialBalanceConfirmed);
    } catch (err: any) {
      console.error('Error updating transaction confirmation:', err);
      window.dispatchEvent(new CustomEvent('show-toast', {
        detail: { message: 'Failed to update tally', type: 'error' }
      }));
    }
  };

  const handleSodaConfirmationChanged = (result: {
    gameId: number;
    allSodasConfirmed: boolean;
    trialBalanceConfirmed?: boolean;
  }) => {
    setReportData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        transactions: prev.transactions.map((t) => {
          const gid = t.matchId || t.matchInfo?.gameId;
          return gid === result.gameId ? { ...t, isConfirmed: result.allSodasConfirmed } : t;
        }),
      };
    });
    rememberTrialBalanceConfirmation(result.trialBalanceConfirmed);
  };

  // Keyboard navigation handler (same as Trial Balance)
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (sodaDialogOpen) return;
      const activeEl = document.activeElement;
      const checkboxFocused = activeEl instanceof HTMLInputElement && activeEl.type === 'checkbox';
      if (!checkboxFocused && (activeEl?.tagName === 'INPUT' || activeEl?.tagName === 'TEXTAREA' || activeEl?.tagName === 'SELECT')) {
        return;
      }

      const hasPartyData =
        reportData &&
        (reportData.transactions.length > 0 ||
          reportData.openingBalance !== undefined ||
          reportData.closingBalance !== undefined);
      const hasPLData = plRowsForDisplay.length > 0;
      if (!hasPartyData && !hasPLData) return;

      // Get all navigable rows (opening + transactions + closing for party; matches for P&L)
      const allRows: Array<{ index: number; type: 'transaction' | 'closing' | 'opening' | 'plmatch' }> = [];

      if (plRowsForDisplay.length > 0) {
        plRowsForDisplay.forEach((_, index) => {
          allRows.push({ index, type: 'plmatch' });
        });
      } else if (reportData) {
        if (reportData.openingBalance !== undefined && reportData.openingBalance !== null) {
          allRows.push({ index: -3, type: 'opening' });
        }
        reportData.transactions.forEach((_, index) => {
          allRows.push({ index, type: 'transaction' });
        });
        if (reportData.closingBalance !== undefined) {
          allRows.push({ index: -2, type: 'closing' });
        }
      }

      if (allRows.length === 0) return;

      let currentIndex = -1;
      if (selectedRowIndex !== null) {
        currentIndex = allRows.findIndex(
          row => row.index === selectedRowIndex
        );
        if (currentIndex === -1) {
          currentIndex = 0; // Default to first row if selected index not found
        }
      }

      if ((e.key === ' ' || e.code === 'Space') && !e.repeat && selectedRowIndex !== null && selectedRowIndex >= 0 && reportData) {
        const transaction = reportData.transactions[selectedRowIndex];
        if (!transaction) return;
        const canConfirm = (transaction.matchId || transaction.matchInfo?.gameId) != null || transaction.transactionId != null;
        if (!canConfirm) return;
        e.preventDefault();
        void handleTransactionConfirmChange(transaction, transaction.isConfirmed !== true);
        return;
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const nextIndex = currentIndex < allRows.length - 1 ? currentIndex + 1 : 0;
        const nextRow = allRows[nextIndex];
        setSelectedRowIndex(nextRow.index);
        
        // Scroll into view
        const rowElement = rowRefs.current.get(nextRow.index);
        if (rowElement) {
          rowElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : allRows.length - 1;
        const prevRow = allRows[prevIndex];
        setSelectedRowIndex(prevRow.index);
        
        // Scroll into view
        const rowElement = rowRefs.current.get(prevRow.index);
        if (rowElement) {
          rowElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      } else if (e.key === 'Enter' && selectedRowIndex !== null) {
        e.preventDefault();
        const selectedRow = allRows.find(row => row.index === selectedRowIndex);
        if (selectedRow && selectedRow.type === 'transaction' && reportData) {
          const transaction = reportData.transactions[selectedRow.index];
          if (transaction) {
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedRowIndex, reportData, plRowsForDisplay, sodaDialogOpen, partyId]);

  // Scroll to the last row only when the account is loaded, not when a tally checkbox updates a row.
  useEffect(() => {
    if (!isOpen) {
      setSelectedRowIndex(null);
      return;
    }

    let targetIndex: number | null = null;

    if (plRowsForDisplay.length > 0) {
      targetIndex = plRowsForDisplay.length - 1;
    } else if (reportData) {
      if (reportData.closingBalance !== undefined && reportData.closingBalance !== null) {
        targetIndex = -2; // closing balance row
      } else if (reportData.transactions.length > 0) {
        targetIndex = reportData.transactions.length - 1;
      } else if (reportData.openingBalance !== undefined && reportData.openingBalance !== null) {
        targetIndex = -3;
      }
    }

    setSelectedRowIndex(targetIndex);

    if (targetIndex === null) return;

    // Scroll after paint so row refs exist
    const scrollToLast = () => {
      const rowElement = rowRefs.current.get(targetIndex!);
      if (rowElement) {
        rowElement.scrollIntoView({ block: 'nearest', behavior: 'auto' });
        return;
      }
      const scroller = tableScrollRef.current;
      if (scroller) {
        scroller.scrollTop = scroller.scrollHeight;
      }
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(scrollToLast);
    });
    const timer = window.setTimeout(scrollToLast, 50);
    return () => window.clearTimeout(timer);
    // reportData is read when a load finishes. Checkbox updates must not re-run this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, loadedReportToken]);

  if (!isOpen) return null;

  const matchTypeToGameType = (tx: PartyAccountTransaction): string | undefined => {
    const m = (tx.matchInfo?.matchType || tx.transactionType || '').toString().toUpperCase();
    if (m === 'O') return GameType.oneday;
    if (m === 'S') return GameType.session;
    if (m === 'T') return GameType.test;
    return undefined;
  };

  const openSodaTransactions = (transaction: PartyAccountTransaction) => {
    const gid = transaction.matchId || transaction.matchInfo?.gameId;
    const pid = transaction.partyId ?? partyId;
    if (!gid || pid == null) return;
    setSodaDialogGameId(gid);
    setSodaDialogPartyId(pid);
    setSodaDialogGameDetails({
      gameType: matchTypeToGameType(transaction),
      teamA: transaction.teamA,
      teamB: transaction.teamB,
      teamC: transaction.teamC,
    });
    setSodaDialogOpen(true);
  };

  const formatPLAmount = (value: number): string => {
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Math.abs(value / 100));
  };

  return (
    <>
    <DialogBox
      lockOverlayScroll
      fullScreen
      isOpen={isOpen}
      onClose={handleDialogClose}
      title={isPLReport ? 'P&L Account Detail' : 'Party Account Detail'}
      panelStyle={{
        width: '100vw',
        maxWidth: '100vw',
        height: '100dvh',
        maxHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
      panelClassName="flex h-[100dvh] w-[100vw] min-h-0 min-w-0 max-h-[100dvh] max-w-[100vw] flex-col overflow-hidden rounded-none"
    >
      <div
        className="flex min-h-0 min-w-0 w-full flex-1 flex-col overflow-hidden bg-white dark:bg-slate-900"
        style={{ flex: '1 1 0', minHeight: 0, maxHeight: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        {/* Header Card */}
        <div className="flex-shrink-0 pt-2 px-2 md:px-4 pb-2" style={{ flexShrink: 0 }}>
          <Card className="overflow-hidden border-none">
            <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-indigo-700 py-2 px-2 sm:px-4 dark:from-slate-800 dark:via-slate-700 dark:to-slate-800">
              {/* Same responsive summary grid pattern as Ledger */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-1 sm:gap-4 text-[11px] sm:text-sm">
                <div className="text-left text-white font-bold uppercase truncate">
                  PCODE: {partyCode}
                </div>
                <div className="text-right sm:text-center text-white font-bold uppercase min-w-0 break-words sm:truncate">
                  NAME: {partyName}
                </div>
                <div className="col-span-2 sm:col-span-1 text-left sm:text-right text-white font-bold uppercase">
                  AMOUNT: <span className="font-normal">{formatAmount(amount)}</span>
                </div>
              </div>
              {(reportData || plReportData) && (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-1 sm:gap-4 pt-2 mt-2 border-t border-white/20 text-[11px] sm:text-sm">
                    <div className="text-left text-white font-bold uppercase">
                      START: {formatDateOnly((reportData || plReportData)!.startingDate)}
                    </div>
                    <div className="text-right sm:text-center text-white font-bold uppercase">
                      END: {formatDateOnly((reportData || plReportData)!.endingDate)}
                    </div>
                    <div className="col-span-2 sm:col-span-1 text-left sm:text-right text-white font-bold uppercase">
                      {plReportData ? 'TOTAL P&L: ' : 'LOSE & WIN: '}
                      <span className="font-normal">
                        {plReportData
                          ? formatPLAmount(plReportData.totalProfitOrLoss ?? 0)
                          : formatAmount(reportData!.loseAndWin || 0)}
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </Card>
        </div>

        {/* Table Section */}
        <div
          className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden px-2 pb-2 pt-2 md:px-4"
          style={{ flex: '1 1 0', minHeight: 0, maxHeight: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
        >
          {loading && (
            <div className="flex flex-col sm:flex-row justify-center items-center py-8 gap-2 sm:gap-0">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-300 border-t-transparent shrink-0"></div>
              <span className="sm:ml-3 text-center sm:text-left text-slate-900 dark:text-slate-100 text-sm sm:text-base font-medium px-2">Loading account details...</span>
            </div>
          )}

          {error && (
            <div className="bg-red-900/80 border-2 border-red-500 text-red-100 px-4 py-3 rounded-lg mb-4 text-sm shadow-lg">
              <span className="font-semibold">Error: </span>{error}
            </div>
          )}

          {!loading && !error && plReportData && plReportData.matches && plReportData.matches.length > 0 && (
            <Card className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden border-none" style={{ flex: 1, minHeight: 0 }}>
              <p className="shrink-0 px-1 mb-1 text-[11px] leading-tight text-slate-500 dark:text-slate-400 md:hidden">
                Scroll vertically · swipe sideways for all columns
              </p>
              <div
                ref={tableScrollRef}
                className="relative max-h-full min-h-0 min-w-0 flex-1 overflow-x-auto overflow-y-auto overscroll-x-contain overscroll-y-contain rounded-b-lg border border-slate-200 dark:border-slate-700 [-webkit-overflow-scrolling:touch] scrollbar-thin scrollbar-thumb-slate-400 dark:scrollbar-thumb-slate-600 scrollbar-track-transparent"
                style={{
                  scrollbarWidth: 'thin',
                  scrollbarColor: 'rgb(148 163 184) transparent',
                  touchAction: 'pan-x pan-y',
                  WebkitOverflowScrolling: 'touch',
                }}
              >
                <div className="flex w-full min-w-[720px] flex-col md:min-w-full">
              {/* P&L Table Header - same structure as Party Account Detail */}
              <div className="sticky top-0 z-10 flex-shrink-0 bg-slate-100/95 dark:bg-slate-800/95 backdrop-blur-sm border-b border-slate-200 dark:border-slate-700">
                <div className="grid grid-cols-[2fr,1fr,1fr,0.5fr,2fr] px-2 sm:px-4 py-2 gap-x-2">
                  <div className="text-xs sm:text-sm font-bold text-blue-600 dark:text-slate-200 uppercase tracking-wide">
                    Date
                  </div>
                  <div className="text-xs sm:text-sm font-bold text-blue-600 dark:text-slate-200 uppercase tracking-wide text-right">
                    Balance
                  </div>
                  <div className="text-xs sm:text-sm font-bold text-blue-600 dark:text-slate-200 uppercase tracking-wide text-center">
                    Loss & Win
                  </div>
                  <div className="w-4 shrink-0"></div>
                  <div className="text-xs sm:text-sm font-bold text-blue-600 dark:text-slate-200 uppercase tracking-wide">
                    Remark
                  </div>
                </div>
              </div>
              <div
                className="divide-y divide-slate-200 dark:divide-slate-700 flex flex-col min-h-0"
              >
                {plRowsForDisplay.map((row, index) => {
                  const { match, runningThrough: runningBalance } = row;
                  const plColor = (match.profitOrLoss ?? 0) >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400';
                  const balanceColor = runningBalance >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400';
                  const remark = `${match.matchLabel || ''}${match.result ? ' - ' + match.result : ''}`.trim() || '-';
                  const isSelected = selectedRowIndex === index;
                  return (
                    <div
                      key={match.gameId || index}
                      ref={(el) => {
                        if (el) rowRefs.current.set(index, el);
                        else rowRefs.current.delete(index);
                      }}
                      className={`grid grid-cols-[2fr,1fr,1fr,0.5fr,2fr] px-2 sm:px-4 py-2 gap-x-2 transition-all duration-200 ${
                        isSelected ? 'bg-blue-200/70 dark:bg-blue-900/50 ring-2 ring-blue-500' : 'hover:bg-slate-200/50 dark:hover:bg-slate-700/50'
                      }`}
                    >
                      <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                        {formatDateOnly(match.matchDate) || '—'}
                      </div>
                      <div className={`text-sm font-medium text-right ${balanceColor}`}>
                        {formatPLAmount(runningBalance)}
                      </div>
                      <div className={`text-sm text-center font-bold ${plColor}`}>
                        {(match.profitOrLoss ?? 0) >= 0 ? '+' : ''}{formatPLAmount(match.profitOrLoss ?? 0)}
                      </div>
                      <div className="w-4"></div>
                      <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                        {remark}
                      </div>
                    </div>
                  );
                })}
                {/* Total row */}
                <div className="grid grid-cols-[2fr,1fr,1fr,0.5fr,2fr] px-2 sm:px-4 py-2 gap-x-2 bg-slate-100/50 dark:bg-slate-800/50 border-t-2 border-slate-200 dark:border-slate-700 font-bold">
                  <div className="text-sm text-slate-900 dark:text-slate-100">Closing Balance</div>
                  <div className={`text-sm text-right ${(plReportData.totalProfitOrLoss ?? 0) >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                    {formatPLAmount(plReportData.totalProfitOrLoss ?? 0)}
                  </div>
                  <div className={`text-sm text-center ${(plReportData.totalProfitOrLoss ?? 0) >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                    {(plReportData.totalProfitOrLoss ?? 0) >= 0 ? '+' : ''}{formatPLAmount(plReportData.totalProfitOrLoss ?? 0)}
                  </div>
                  <div className="w-4"></div>
                  <div className="text-sm text-slate-900 dark:text-slate-100">-</div>
                </div>
              </div>
                </div>
              </div>
            </Card>
          )}

          {!loading && !error && plReportData && (!plReportData.matches || plReportData.matches.length === 0) && (
            <div className="text-slate-600 dark:text-slate-400 text-center py-8">
              No P&L data available for the selected period
            </div>
          )}

          {!loading && !error && reportData && (
            <Card className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden border-none" style={{ flex: 1, minHeight: 0 }}>
              <p className="shrink-0 px-1 mb-1 text-[11px] leading-tight text-slate-500 dark:text-slate-400 md:hidden">
                Scroll vertically · swipe sideways for all columns
              </p>
              <div
                ref={tableScrollRef}
                className="relative max-h-full min-h-0 min-w-0 flex-1 overflow-x-auto overflow-y-auto overscroll-x-contain overscroll-y-contain rounded-b-lg border border-slate-200 dark:border-slate-700 [-webkit-overflow-scrolling:touch] scrollbar-thin scrollbar-thumb-slate-400 dark:scrollbar-thumb-slate-600 scrollbar-track-transparent"
                style={{
                  scrollbarWidth: 'thin',
                  scrollbarColor: 'rgb(148 163 184) transparent',
                  touchAction: 'pan-x pan-y',
                  WebkitOverflowScrolling: 'touch',
                }}
              >
                <div className="flex w-full min-w-[900px] flex-col md:min-w-full">
              {/* Table Header — sticky; wide min-width + horizontal scroll on narrow viewports (same idea as Ledger) */}
              <div className="sticky top-0 z-10 flex-shrink-0 bg-slate-100/95 dark:bg-slate-800/95 backdrop-blur-sm border-b border-slate-200 dark:border-slate-700">
                <div className="grid grid-cols-[2fr,1fr,1fr,1fr,0.5fr,minmax(0,4fr),auto,auto] px-2 sm:px-4 py-2 gap-x-2">
                  <div className="text-xs sm:text-sm font-bold text-blue-600 dark:text-slate-200 uppercase tracking-wide">
                    Date
                  </div>
                  <div className="text-xs sm:text-sm font-bold text-blue-600 dark:text-slate-200 uppercase tracking-wide text-right">
                    Profit/Loss
                  </div>
                  <div className="text-xs sm:text-sm font-bold text-blue-600 dark:text-slate-200 uppercase tracking-wide text-right">
                    Balance
                  </div>
                  <div className="text-xs sm:text-sm font-bold text-blue-600 dark:text-slate-200 uppercase tracking-wide text-center">
                    Loss & Win
                  </div>
                  <div className="w-4 shrink-0"></div>
                  <div className="text-xs sm:text-sm font-bold text-blue-600 dark:text-slate-200 uppercase tracking-wide">
                    Remark
                  </div>
                  <div className="text-xs sm:text-sm font-bold text-blue-600 dark:text-slate-200 uppercase tracking-wide text-center w-12">
                  </div>
                  <div className="text-xs sm:text-sm font-bold text-blue-600 dark:text-slate-200 uppercase tracking-wide text-center w-12">
                  </div>
                </div>
              </div>

              <div className="divide-y divide-slate-200 dark:divide-slate-700 flex flex-col min-h-0">
                {/* Opening balance — first row from API `openingBalance` (minor units, same as balance) */}
                {reportData.openingBalance !== undefined && reportData.openingBalance !== null && (
                  <div
                    key="opening-balance"
                    ref={(el) => {
                      if (el) {
                        rowRefs.current.set(-3, el);
                      } else {
                        rowRefs.current.delete(-3);
                      }
                    }}
                    className={`grid grid-cols-[2fr,1fr,1fr,1fr,0.5fr,minmax(0,4fr),auto,auto] px-2 sm:px-4 py-2 transition-all duration-200 gap-x-2 ${
                      selectedRowIndex === -3
                        ? 'bg-blue-200/70 dark:bg-blue-900/50 ring-2 ring-blue-500'
                        : 'bg-slate-50/80 dark:bg-slate-800/40 hover:bg-slate-200/50 dark:hover:bg-slate-700/50'
                    }`}
                  >
                    <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                      {formatDateOnly(reportData.startingDate) || '—'}
                    </div>
                    <div className="text-sm font-medium text-right text-slate-500 dark:text-slate-400">—</div>
                    <div className={`text-sm font-medium text-right ${getBalanceColor(reportData.openingBalance)}`}>
                      {formatAmount(reportData.openingBalance)}
                    </div>
                    <div className="text-sm text-center font-medium text-slate-500 dark:text-slate-400">—</div>
                    <div className="w-4 shrink-0"></div>
                    <div className="text-sm font-semibold text-slate-700 dark:text-slate-200 min-w-0 break-words">
                      Opening Balance
                    </div>
                    <div className="text-center w-12 flex items-center justify-center"></div>
                    <div className="text-center w-12 flex items-center justify-center"></div>
                  </div>
                )}

                {reportData.transactions.map((transaction, index) => {
                  const isMatch =
                    transaction.transactionType === 'S' ||
                    transaction.transactionType === 'O' ||
                    transaction.transactionType === 'T';
                  const isHavala = transaction.transactionType === 'H';
                  const gameIdForSoda = transaction.matchId || transaction.matchInfo?.gameId;
                  const partyIdForSoda = transaction.partyId ?? partyId;
                  const showSodaEye = Boolean(isMatch && gameIdForSoda && partyIdForSoda);
                  const { label: lossWinLabel, tone: lossWinTone } = getLossWinFromCrDr(
                    transaction.credit ?? 0,
                    transaction.debit ?? 0,
                  );
                  const drMinusCr =
                    (transaction.debit ?? 0) - (transaction.credit ?? 0);
                  const showWinLoss = isMatch && !isHavala;

                  const isSelected = selectedRowIndex === index;
                  return (
                    <div
                      key={transaction.id || index}
                      ref={(el) => {
                        if (el) {
                          rowRefs.current.set(index, el);
                        } else {
                          rowRefs.current.delete(index);
                        }
                      }}
                      className={`grid grid-cols-[2fr,1fr,1fr,1fr,0.5fr,minmax(0,4fr),auto,auto] px-2 sm:px-4 py-2 transition-all duration-200 gap-x-2 ${
                        isSelected 
                          ? 'bg-blue-200/70 dark:bg-blue-900/50 ring-2 ring-blue-500' 
                          : 'hover:bg-slate-200/50 dark:hover:bg-slate-700/50'
                      }`}
                    >
                      <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                        {formatDateOnly(transaction.date) || '—'}
                      </div>
                      <div
                        className={`text-sm text-right font-semibold ${
                          drMinusCr < 0
                            ? 'text-emerald-600 dark:text-emerald-400'
                            : drMinusCr > 0
                              ? 'text-red-600 dark:text-red-400'
                              : 'text-slate-600 dark:text-slate-300'
                        }`}
                      >
                        {formatSignedProfitLoss(drMinusCr)}
                      </div>
                      <div className={`text-sm font-medium text-right ${getBalanceColor(transaction.balance)}`}>
                        {formatAmount(transaction.balance)}
                      </div>
                      <div
                        className={`text-sm text-center font-bold ${
                          !showWinLoss
                            ? 'text-slate-900 dark:text-slate-100'
                            : lossWinTone === 'win'
                            ? 'text-emerald-600 dark:text-emerald-400'
                            : lossWinTone === 'loss'
                              ? 'text-red-600 dark:text-red-400'
                              : 'text-slate-900 dark:text-slate-100'
                        }`}
                      >
                        {showWinLoss ? lossWinLabel : ''}
                      </div>
                      <div className="w-4 shrink-0"></div>
                      <div className="text-sm font-medium text-slate-900 dark:text-slate-100 min-w-0 break-words">
                        {transaction.remark || (
                          <div className="flex gap-2">
                            {isMatch && transaction.matchId && (
                              <>
                                <span>{transaction.otherPartyCode || ''}</span>
                                <span>{transaction.matchTypeId || ''}</span>
                                <span className="border-l border-slate-400 pl-2">{transaction.partyId || ''}</span>
                              </>
                            )}
                            {isHavala && transaction.havalaId && (
                              <>
                                <span>{transaction.otherPartyCode || ''}</span>
                                <span className="border-l border-slate-400 pl-2">{transaction.havalaId}</span>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="text-center w-12 flex items-center justify-center">
                        {showSodaEye ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openSodaTransactions(transaction);
                            }}
                            className="p-1 rounded bg-slate-200 dark:bg-slate-600 hover:bg-slate-300 dark:hover:bg-slate-500 text-slate-800 dark:text-slate-100 transition-colors"
                            title="View soda transactions"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                        ) : null}
                      </div>
                      <div className="text-center w-12 flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500"
                          checked={transaction.isConfirmed === true}
                          disabled={(transaction.matchId || transaction.matchInfo?.gameId) == null && transaction.transactionId == null}
                          onChange={(e) => {
                            handleTransactionConfirmChange(transaction, e.target.checked);
                          }}
                        />
                      </div>
                    </div>
                  );
                })}

                {/* Closing Balance — display balance from last transaction (running balance), not API closingBalance */}
                {reportData.closingBalance !== undefined && (() => {
                  const lastTx = reportData.transactions?.length ? reportData.transactions[reportData.transactions.length - 1] : null;
                  const displayBalance = lastTx != null ? lastTx.balance : reportData.openingBalance ?? 0;
                  return (
                  <div
                    ref={(el) => {
                      if (el) {
                        rowRefs.current.set(-2, el);
                      } else {
                        rowRefs.current.delete(-2);
                      }
                    }}
                    className={`grid grid-cols-[2fr,1fr,1fr,1fr,0.5fr,minmax(0,4fr),auto,auto] px-4 py-2 gap-x-2 transition-all duration-200 ${
                      selectedRowIndex === -2 
                        ? 'bg-blue-200/70 dark:bg-blue-900/50 ring-2 ring-blue-500' 
                        : 'bg-slate-100/50 dark:bg-slate-800/50'
                    }`}
                  >
                    <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                      {formatDateOnly(reportData.endingDate) || '—'}
                    </div>
                    <div className="text-sm font-medium text-right text-slate-500 dark:text-slate-400">—</div>
                    <div className={`text-sm font-medium text-right ${getBalanceColor(displayBalance)}`}>
                      {formatAmount(displayBalance)}
                    </div>
                    <div className="text-sm text-center font-medium text-slate-900 dark:text-slate-100">-</div>
                    <div className="w-4"></div>
                    <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                      Closing Balance..
                    </div>
                    <div className="w-12"></div>
                    <div className="w-12"></div>
                  </div>
                  );
                })()}
              </div>
                </div>
              </div>
            </Card>
          )}

          {!loading && !error && !reportData && !plReportData && (
            <div className="text-slate-600 dark:text-slate-400 text-center py-8">
              No account data available
            </div>
          )}
        </div>
      </div>
    </DialogBox>

    {sodaDialogOpen && sodaDialogGameId && sodaDialogPartyId ? (
      <PartySodaTransactionsDialog
        isOpen={sodaDialogOpen}
        onClose={() => {
          setSodaDialogOpen(false);
          setSodaDialogGameDetails(null);
        }}
        gameId={sodaDialogGameId}
        partyId={sodaDialogPartyId}
        gameDetails={sodaDialogGameDetails ?? undefined}
        onConfirmationChanged={handleSodaConfirmationChanged}
      />
    ) : null}
    </>
  );
};

export default PartyAccountDetail;

export {};
